/**
 * StandPills.jsx — Stand bilgi pillerinin TEK kaynağı
 *
 * Stand Listesi (StandListPage.jsx) ile Stand Detayı (StandOverviewDetailPage.jsx)
 * aynı standı anlatıyor; 2026-08-29'da backend pilleri gönderdiğinde sadece LİSTE
 * güncellendi ve iki sayfa birbirinden kaydı — detay hâlâ "İnşaat İzni Yok" derken
 * liste aynı stand için "İzin Bekliyor" diyordu. Bu dosya o ikiliği kapatıyor:
 * pillerin sözlüğü, izin makineleri ve "bu standda sorun var mı" kararı burada
 * TANIMLI, iki sayfa da buradan okuyor.
 *
 * Veri kaynağı `GET /standTalep/list` satırı (StandListItemDTO) — detay sayfası onu
 * standOverviewService.getStandRosterRow ile ayrıca çeker, çünkü
 * `GET /standTalep/detay` bu alanların hiçbirini taşımıyor.
 * Alan anlamları: docs/guides/FE-missing-pills-guide.md.
 */
import React from "react";
import { Badge, UncontrolledPopover, PopoverBody } from "reactstrap";
import {
  Building2, Globe, Flag, Package, Info, Layers3, Layers, Puzzle,
  Maximize2, Ruler, Zap, Box, Image as ImageIcon, HardHat, Droplet, Wind, Wifi,
} from "lucide-react";
import { resolvePillLabel } from "../../services/standOverviewService";
import { DEAL_STATUS, FINANCE_APPROVAL } from "../../services/projectService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

// ─── Stand tipi pilleri — stand_type ağacının 4 seviyesi ─────────────────────
// (bkz. missing-pills-guide.md §4): canlı roster bu dördünü id + AD olarak döndürüyor.
// Aşağıdaki haritalar artık SADECE mock kodlarını ('paket'/'moduler'/'maxima')
// yerelleştirmek için var — canlı adlar ("Özel Stand", "Sunta - Boya", "Örümcek
// Stand") olduğu gibi basılır, çünkü katalog KAPALI BİR KÜME DEĞİL ve adları staff
// düzenleyebiliyor. İkon bu yüzden değere göre değil SEVİYEYE göre sabit: yeni bir
// katalog girdisi frontend sürümü gerektirmesin diye.
export const STAND_CATEGORY_LABELS = { get paket() { return i18n.t("components:standPills.paket"); }, get pavilyon() { return i18n.t("components:standPills.pavilyon"); }, get ozel() { return i18n.t("components:standPills.ozel"); } };
export const PROJECT_TYPE_LABELS = { get moduler() { return i18n.t("components:standPills.moduler"); }, get ahsap() { return i18n.t("components:standPills.ahsap"); }, get diger() { return i18n.t("components:standPills.diger"); } };
export const PROJECT_STRUCTURE_LABELS = {
  maxima: "Maxima", octonorm: "Octonorm", mdf: "MDF", get sunta() { return i18n.t("components:standPills.sunta"); }, get agac() { return i18n.t("components:standPills.agac"); },
};

/**
 * Paralel ağaç işaretini (" (n)") stand tipi adından atar.
 *
 * Roster adları sunucu tarafında zaten temizlenmiş geliyor (bkz. FE-StandTalepController-
 * guide.md: her stand tipi adı " (n)" paralel-ağaç işareti ATILMIŞ olarak gelir), ama
 * katalog uçları (`getStandTypePath`/`getStandTypes`) HAM adı veriyor. Detay sayfası
 * ağaç kırıntısını katalogdan çektiği için aynı stand listede "Paket Stand", detayda
 * "Paket Stand (n)" yazıyordu — hangi ağaçtan geldiğini **id** söyler, ad değil.
 */
export function stripParallelTreeMarker(name) {
  if (typeof name !== "string") return name;
  return name.replace(/\s*\(n\)\s*$/, "");
}

/**
 * Stand tipi YENİ ağaçtan mı (id 20-59, " (n)" işaretli) yoksa eski ağaçtan mı (id 1-19)?
 * stand_type tablosunda ağacı söyleyen bir kolon yok; tek işaret Türkçe adın " (n)"
 * soneki (util/StandNaming.java). Okuma SQL'i bu soneki iki dilde de geri ekliyor, yani
 * ham `stand_type_name` yeterli; `stand_type_name_tr` varsa o önceliklidir.
 */
export function isNewTreeStandType(type) {
  const name = type?.stand_type_name_tr ?? type?.stand_type_name;
  return typeof name === "string" && /\(n\)\s*$/.test(name);
}

/** Bir tip listesini yeni / eski ağaç diye ikiye böler; sıra korunur. */
export function splitStandTypesByTree(list) {
  const fresh = [];
  const legacy = [];
  (list || []).forEach((t) => (isNewTreeStandType(t) ? fresh : legacy).push(t));
  return { fresh, legacy };
}

// Dikey ayraç — bootstrap'ın .vr utility sınıfı bu temanın derlenmiş CSS'inde
// width:0 hesaplıyor (muhtemelen Velzon'un utility seçimi bunu içermiyor), o yüzden
// class yerine inline stil kullanılıyor; alignSelf:"stretch" ile flex satırının
// tam boyuna uzanır.
export function VDivider({ className = "" }) {
  return (
    <div
      className={className}
      style={{ width: 1, alignSelf: "stretch", backgroundColor: "currentColor", opacity: 0.15 }}
    />
  );
}

// Bilgi rozeti: her zaman teal (success) — Yerli/Yabancı, Paket/Pavilyon/Özel,
// proje tipi ve proje yapısı için; değer değil sadece etiket/ikon değişir.
export function InfoPill({ icon: Icon, label, className = "me-2 mb-1" }) {
  return (
    <Badge color="success" className={`d-inline-flex align-items-center gap-1 px-2 py-1 ${className}`}>
      <Icon size={12} />{label}
    </Badge>
  );
}

// Gri bilgi kutusu — Firma tarafı listelerinin (FirmaProjelerListPage/FirmaStandListPage)
// "durum değil, bilgi" dili: her zaman nötr gri, sadece etiket/ikon değişir. O sayfalar
// dosya-yerel kopyalarını taşıyor; denetçi tarafında (Standlar listesi) buradan okunur.
// `title` verilirse üzerine gelince açıklama gösterir (ör. "örnek alan").
// #488 `truncate`: sabit genişlikli bir sütunda (Standlarım, 200 px) uzun bir katalog adı
// yanındaki sütuna taşmasın diye tek satırda "…" ile kesilir; tam metin title olarak kalır.
export function InfoBox({ icon: Icon, label, title, truncate = false }) {
  return (
    <div
      className={`d-flex align-items-center gap-1 rounded-3 px-2 py-1 text-white small fw-semibold text-nowrap${truncate ? "" : " flex-shrink-0"}`}
      style={{ background: NEUTRAL_GRAY, minWidth: truncate ? 0 : undefined }}
      title={title ?? (truncate ? label : undefined)}
    >
      <Icon size={12} className="flex-shrink-0" />
      {truncate ? <span className="text-truncate">{label}</span> : label}
    </div>
  );
}

export function StandBadgeRow({ s }) {
  const { t } = useTranslation("components");
  const category = resolvePillLabel(s.standCategory, STAND_CATEGORY_LABELS);
  const projectType = resolvePillLabel(s.projectType, PROJECT_TYPE_LABELS);
  const structureLabel = resolvePillLabel(s.projectStructure, PROJECT_STRUCTURE_LABELS);
  return (
    <div className="d-flex flex-wrap">
      {/* 2026-08-29 DÜZELTME: bu rozet koşulsuz render ediliyordu — canlı satırlarda
          firmaNation yoktu, undefined !== 1 olduğu için HER stand "Yabancı" yazıyordu.
          Roster artık alanı taşıyor (1 Yerli · 2 Yabancı · null bilinmiyor); null
          gelirse kardeşleri gibi gizlenir. */}
      {s.firmaNation != null && (
        <InfoPill icon={s.firmaNation === 1 ? Flag : Globe} label={s.firmaNation === 1 ? t("standPills.yerli") : t("standPills.yabanci")} />
      )}
      {category && <InfoPill icon={Package} label={category} />}
      {/* standTypeName — gerçek stand_type adı (ör. "Sunta - MDF Lam" ya da henüz
          sistem atanmadıysa "Özel Stand" kökü); mock'un kurgusal Paket/Pavilyon/Özel
          + moduler/ahşap kategorileriyle karışmasın diye ayrı bir rozet. */}
      {s.standTypeName && <InfoPill icon={Layers3} label={stripParallelTreeMarker(s.standTypeName)} />}
      {projectType && <InfoPill icon={Layers} label={projectType} />}
      {structureLabel && <InfoPill icon={Puzzle} label={structureLabel} />}
    </div>
  );
}

// Liste satırlarına ve Stand Detayı'nın Proje Bilgiler sekmesine özel, 6+ rozetli
// bilgi satırı — Kart (grid) görünümü hâlâ yukarıdaki StandBadgeRow'un kısa halini
// (Yerli/Yabancı + kategori/tip/yapı) kullanıyor. Yerli/Yabancı rozeti burada kasıtlı
// olarak yok — alan/yükseklik/kat bilgisine yer açmak için çıkarıldı.
// 2 sütunlu grid (referans görseldeki düzen) — InfoPill'in varsayılan me-2/mb-1
// boşluğu grid'in kendi gap'iyle çakışmasın diye className="" ile kapatılıyor.
// `showKw` varsayılan olarak KAPALI: Stand Listesi satırı zaten yoğun ve kW oraya
// hiç girmemişti; Stand Detayı'nda ise beyanın bir parçası olarak anlamlı (bkz.
// DeclaredSpecStrip'teki "Elektrik" kutusuyla aynı sayı, orada beyan bağlamında).
export function StandBadgeRowFull({ s, columns = 2, showKw = false }) {
  const category = resolvePillLabel(s.standCategory, STAND_CATEGORY_LABELS);
  const projectType = resolvePillLabel(s.projectType, PROJECT_TYPE_LABELS);
  const structureLabel = resolvePillLabel(s.projectStructure, PROJECT_STRUCTURE_LABELS);
  return (
    <div className="d-grid" style={{ gridTemplateColumns: `repeat(${columns}, auto)`, gap: "6px 8px" }}>
      {category && <InfoPill icon={Package} label={category} className="" />}
      {s.standTypeName && <InfoPill icon={Layers3} label={stripParallelTreeMarker(s.standTypeName)} className="" />}
      {s.floorInfo && <InfoPill icon={Building2} label={s.floorInfo} className="" />}
      {/* ⚠️ projectType bugün canlıda HER satırda null (17.077 tipli proje hâlâ eski
          üç seviyeli ağaçta) — pil bilerek gizleniyor, fallback UYDURULMUYOR. */}
      {projectType && <InfoPill icon={Layers} label={projectType} className="" />}
      {structureLabel && <InfoPill icon={Puzzle} label={structureLabel} className="" />}
      {/* standArea KROKİDEN ölçülen alan (beyan edilen m² değil) ve fuarların çoğunda
          null — "—" değil, pil hiç görünmüyor; "0 m²" ASLA basılmaz. */}
      {s.standArea != null && <InfoPill icon={Maximize2} label={`${s.standArea} m²`} className="" />}
      {s.projectHeightNumber != null && <InfoPill icon={Ruler} label={`${s.projectHeightNumber} m`} className="" />}
      {/* null = elektrik istenmedi. "0 kW" rozeti "hiç istemedi"den FARKLI bir cümledir. */}
      {showKw && s.kw != null && <InfoPill icon={Zap} label={`${s.kw} kW`} className="" />}
    </div>
  );
}

// İnşa İzni rozeti — 2026-08-29'dan beri boolean DEĞİL, açık uçlu bir durum arama
// tablosu (missing-pills-guide.md §5): "İnşa İznine Gerek yok" ile "İzin Verilmedi"
// ikisi de granted:false ama zıt anlamlı, tek bir boolean bu bilgiyi çöpe atıyordu.
// ⚠️ Renk backend'den gelir (stand_status.badge) — statusId'den YENİDEN TÜRETME:
// staff yeni bir durum eklediğinde rengi frontend sürümü olmadan gelsin diye.
// statusId 0 (4.448 canlı satır) → statusName/badge ikisi de null, "—" basılır.
export function permitBadge(s) {
  const p = s.constructionPermit;
  if (p?.statusName && p?.badge) return { label: p.statusName, color: p.badge };
  if (p) return { label: "—", color: "secondary" };           // statusId 0 / bilinmiyor
  // Mock kayıtlarda constructionPermit nesnesi yok — eski boolean'a düş.
  return s.constructionPermitGranted
    ? { label: i18n.t("components:standPills.verildi"), color: "success" }
    : { label: i18n.t("components:standPills.verilmedi"), color: "danger" };
}

// İş İzni — jobApproved/jobTotal DEĞİL (§6: onlar her stand_job satırını sayar,
// üçte ikisi hizmet/legacy). requested, "hiç istemedi"yi "reddedildi"den ayırır.
export function workPermitBadge(s) {
  const w = s.workPermit;
  if (w && w.requested === 0) return { label: i18n.t("components:standPills.talepEdilmedi"), color: "secondary" };
  if (w) {
    if (w.granted) return { label: i18n.t("components:standPills.verildi"), color: "success" };
    if (w.rejected > 0) return { label: i18n.t("components:standPills.reddedildi"), color: "danger" };
    return { label: i18n.t("components:standPills.bekliyor", { pending: w.pending }), color: "warning" };
  }
  return s.workPermitGranted
    ? { label: i18n.t("components:standPills.verildi"), color: "success" }
    : { label: i18n.t("components:standPills.verilmedi"), color: "danger" };
}

// Dekoratör atanmamışsa ya da borç/izin rozetlerinden biri "kötü" ise satır/kart
// kenarlığı (detayda banner kenarlığı) kırmızıya döner — proje kaydı olmayan standlar
// bu hesaba dahil değil.
// ⚠️ "Bilinmiyor" (statusId 0) ve "hiç talep edilmedi" SORUN DEĞİL: 4.448 canlı satır
// statusId 0 taşıyor, bunları kırmızıya boyamak listeyi baştan sona kırmızı yapardı.
// Sorun = açıkça kötü olan durum: izin bekliyor (2) / verilmedi (3), ya da reddedilmiş
// bir iş izni talebi.
export function hasIssue(s) {
  if (!s.firmaId) return false;
  const permitBad = s.constructionPermit
    ? [2, 3].includes(s.constructionPermit.statusId)
    : !s.constructionPermitGranted;
  const workBad = s.workPermit
    ? s.workPermit.rejected > 0
    : !s.workPermitGranted;
  return !s.dekoratorFirmaName || s.hasDebt || permitBad || workBad;
}

// Ek bilgi göstergesi: denetçinin her zaman görmesi gerekmeyen bilgileri (anlaşma
// durumu, finans onayı, borç/inşa izni/iş izni) tek bir küçük ikonda toplar — üzerine
// gelince (hover) bir Popover içinde açılır. Proje durumu (statusId/PROJECT_STATUS)
// kasıtlı olarak burada değil — ileride kart/kutu etrafında bir renkle ifade edilecek,
// o karar verilene kadar hiçbir yerde render edilmiyor. İkonun kendisi hasIssue ile
// aynı sinyali taşır (kırmızı = dikkat gereken bir şey var) ki denetçi hover yapmadan
// da bir şeyin göze çarpması gerekip gerekmediğini anlayabilsin.
//
// `domId` verilmezse satır kimliğinden türetilir — aynı sayfada iki gösterge
// (ör. liste kartı + detay banner'ı) çakışmasın diye çağıran taraf ezebilir.
export function StandExtraInfoIndicator({ s, domId }) {
  const { t } = useTranslation("components");
  if (!s.firmaId) return null;
  const targetId = domId || `stand-extra-${s.id}`;
  const issue = hasIssue(s);
  const deal = DEAL_STATUS[s.dealStatus];
  const finance = FINANCE_APPROVAL[s.financeApproval];
  const rows = [
    { label: t("standPills.anlasmaDurumu"), badge: deal },
    { label: t("standPills.finansOnayi"),   badge: finance },
    { label: t("standPills.borc"),           badge: s.hasDebt ? { label: t("standPills.var"), color: "danger" } : { label: t("standPills.yok"), color: "success" } },
    { label: t("standPills.insaIzni"),      badge: permitBadge(s) },
    { label: t("standPills.isIzni"),        badge: workPermitBadge(s) },
  ];
  return (
    <>
      <span
        id={targetId}
        className={`d-inline-flex align-items-center justify-content-center rounded-circle border ${issue ? "border-danger text-danger" : "border-secondary text-secondary"}`}
        style={{ width: 26, height: 26, cursor: "default" }}
      >
        <Info size={14} />
      </span>
      <UncontrolledPopover target={targetId} trigger="hover focus" placement="top">
        <PopoverBody style={{ minWidth: 200 }}>
          {rows.map((r) => (
            <div key={r.label} className="d-flex align-items-center justify-content-between gap-3 mb-1">
              <span className="small text-muted">{r.label}</span>
              {r.badge && <Badge color={r.badge.color} className="ms-2">{r.badge.label}</Badge>}
            </div>
          ))}
        </PopoverBody>
      </UncontrolledPopover>
    </>
  );
}

/**
 * Belge durumu kutularının okuduğu üç `stand_file` başlığı.
 *
 * ⚠️ CANLI VE MOCK İD'LERİ FARKLI, ve bu 2026-09-04'e kadar bir HATA kaynağıydı:
 * Stand Listesi üçlüyü `5 Proje Görselleri · 3 Teknik Talep Planı · 2 Statik Hesap`
 * diye sabitlemişti — bunlar MOCK id'leri. Canlı katalogda (bkz.
 * FE-ProjectController-beyani-revisions-guide.md §"stand_file ids"):
 *   1 2-D Görseller · 2 3-D Görseller · 3 Elektrik Planı · 5 Taahütnameler · 7 Statik Hesap
 * yani liste, TAAHÜTNAMELER'in onay durumunu "Proje Görselleri" etiketiyle,
 * 3-D GÖRSELLER'inkini de "Statik Hesap" etiketiyle basıyordu. Stand Detayı zaten
 * doğru üçlüyü (2/3/7) kullanıyordu — iki sayfa aynı standa farklı şeyler diyordu.
 *
 * Taahütnameler (5) bilerek bu listede yok: onun kendi kutuları var (üretim zinciri,
 * COMMITMENT_STATE) ve tek bir onay durumuna sığmıyor.
 */
export function getDocStatusGroups(isLive) {
  return isLive
    ? [
        { standFileId: 2, label: i18n.t("components:standPills.projeGorselleri"), icon: ImageIcon },
        { standFileId: 3, label: i18n.t("components:standPills.teknikTalepPlani"), icon: Zap },
        { standFileId: 7, label: i18n.t("components:standPills.statikHesap"), icon: Box },
      ]
    : [
        // Mock veri seti kendi id'lerini kullanıyor (görseller 5'te, statik 2'de).
        { standFileId: 5, label: i18n.t("components:standPills.projeGorselleri"), icon: ImageIcon },
        { standFileId: 3, label: i18n.t("components:standPills.teknikTalepPlani"), icon: Zap },
        { standFileId: 2, label: i18n.t("components:standPills.statikHesap"), icon: Box },
      ];
}

// ─── Durum karesi: renk, biçim ve ölçünün TEK tanımı ─────────────────────────
// Referans /Firma/Projeler (FirmaProjelerListPage.StatusTile) ve kardeşi
// FirmaStandListPage.StatusTile: 40×40, rounded-3, 18px beyaz ikon. Denetçi tarafı
// (Stand Listesi 30×30 + `var(--vz-*)`, Stand Detayı 34/40/50 + kendi hex kopyası)
// bu ölçüden ve paletten kaymıştı — 2026-09-04'te üçü de buraya bağlandı.
//
// ⚠️ RENKLER SABİT HEX, CSS DEĞİŞKENİ DEĞİL. Bu temanın çalışma zamanında
// `--bs-*` değişkenleri yok; `--vz-*` VAR ama tonları farklı (ör. --vz-warning
// rgb(255,127,65), buradaki warning #f7b84b). Stand Listesi `var(--vz-*)`
// kullandığı için aynı "Beklemede" durumu firma tarafında sarı-turuncu, denetçi
// tarafında kırmızı-turuncu görünüyordu.
export const NEUTRAL_GRAY = "#878a99";
// `info` (mavi) taahhütname üretim zinciriyle geldi — "kaynak seçildi"/"üretildi"
// beklemede de değil onaylanmış da değil, ara bir durum (bkz. COMMITMENT_STATE).
// `secondary` KASITLI OLARAK YOK: NEUTRAL_GRAY'e düşsün diye (firma tarafındaki
// aynı tercih) — "kayıt yok" ile "durumu gri olan bir durum" aynı griyi paylaşır.
export const STATUS_HEX = {
  warning: "#f7b84b", success: "#0ab39c", danger: "#f06548", info: "#299cdb",
};

// Bir rozet rengi adını ("warning"/"success"/...) kare arka planına çevirir.
// Bilinmeyen ad ya da null → nötr gri.
export function statusBackground(color) {
  return STATUS_HEX[color] ?? NEUTRAL_GRAY;
}

export const STATUS_TILE_SIZE = 40;
export const STATUS_TILE_ICON = 18;

// StatusTile'ın `marker` noktası (#268): karenin sağ üst köşesinde, kart rengiyle çerçeveli küçük daire.
export function TileMarker({ renk }) {
  return (
    <span
      aria-hidden="true"
      style={{
        position: "absolute", top: -4, right: -4, width: 12, height: 12, borderRadius: "50%",
        background: renk, border: "2px solid var(--vz-card-bg, #fff)",
      }}
    />
  );
}

/**
 * Durum karesi. `color` bir rozet rengi adı ("warning"), `background` ise doğrudan
 * bir hex — ikisinden biri verilir (background öncelikli, izin/borç gibi ikili
 * durumlar için firma tarafı doğrudan STATUS_HEX.success/danger geçiyor).
 *
 * onClick verilirse kare klavyeyle de çalışır (Enter/Space) — firma tarafındaki
 * StatusTile ile aynı sözleşme.
 *
 * `href` verilirse kare YENİ SEKMEDE açılan gerçek bir bağlantı olur (`<a target="_blank">`, backlog
 * #273): ctrl / orta tuş da çalışır, onClick'ten farkı bu. href onClick'e göre önceliklidir.
 *
 * `marker` (#268) bir hex renk verilirse karenin sağ üst köşesine küçük bir nokta konur: kare rengi durumu söylerken
 * "onaylı ama dikkat" gibi ikinci bir bilgi için (ör. onaylı ama eski SGK Hizmet Dökümü). Açıklama yine `label`da.
 *
 * `caption` (#531) verilirse karenin altına "SGK" / "KVKK" gibi küçük bir yazı konur (kare + yazı dikey bir yığın olur); verilmezse
 * kare eskisi gibi tek başına basılır.
 *
 * `tour` verilirse karenin kendisi `data-tour` işareti taşır: firma portalı kullanım turu (services/egitimTuru.js) kareyi vurgular.
 */
export function StatusTile({
  icon: Icon, color, background, opacity = 1, label, onClick, id, className = "",
  interactive: interactiveProp, href, marker, caption, tour,
}) {
  const withCaption = (tile) => (caption ? (
    <div className="d-flex flex-column align-items-center gap-1 flex-shrink-0">
      {tile}
      <span className="text-muted text-nowrap" style={{ fontSize: "0.65rem", lineHeight: 1 }}>{caption}</span>
    </div>
  ) : tile);
  if (href) {
    return withCaption(
      <a
        id={id}
        data-tour={tour}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 text-decoration-none ${marker ? "position-relative" : ""} ${className}`}
        style={{
          width: STATUS_TILE_SIZE, height: STATUS_TILE_SIZE,
          background: background ?? statusBackground(color),
          opacity,
          cursor: "pointer",
        }}
        title={label}
      >
        <Icon size={STATUS_TILE_ICON} color="#fff" />
        {marker && <TileMarker renk={marker} />}
      </a>
    );
  }
  // `interactive` olmadan da onClick tıklanabilir yapar; ayrı bir bayrak olmasının
  // sebebi Stand Listesi: oradaki kare bir Popover'ın hedefi (trigger="legacy"),
  // yani tıklamayı reactstrap kendi bağlıyor — kare yine de tıklanabilir GÖRÜNMELİ.
  const interactive = interactiveProp ?? typeof onClick === "function";
  return withCaption(
    <div
      id={id}
      data-tour={tour}
      className={`d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 ${marker ? "position-relative" : ""} ${className}`}
      style={{
        width: STATUS_TILE_SIZE, height: STATUS_TILE_SIZE,
        background: background ?? statusBackground(color),
        opacity,
        cursor: interactive ? "pointer" : undefined,
      }}
      title={label}
      {...(interactive
        ? {
            role: "button",
            tabIndex: 0,
            ...(typeof onClick === "function"
              ? {
                  onClick,
                  onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); }
                  },
                }
              : {}),
          }
        : {})}
    >
      <Icon size={STATUS_TILE_ICON} color="#fff" />
      {marker && <TileMarker renk={marker} />}
    </div>
  );
}

// Taahhütname kareleri — firma tarafındaki COMMITMENT_GROUPS ile aynı ikon eşlemesi.
// Statik/Elektrik ilgili belge grubuyla aynı ikonu paylaşır (aynı konuya işaret
// ettiklerini vurgulamak için), İSG'nin karşılığı bir belge grubu olmadığından HardHat.
export const COMMITMENT_ICONS = { isg: HardHat, statik: Box, elektrik: Zap };

// Teknik talep ikonları (Elektrik/Su/Hava/İnternet) — backlog #69: aynı harita
// FirmaTalepStandDetailPage.jsx, FirmaTaleplerListPage.jsx, FirmaStandListPage.jsx,
// FirmaProjelerListPage.jsx ve StandDetayPage.jsx içinde ayrı ayrı kopyalanmıştı; TEK kaynak burası.
// Anahtarlar hem stand_talep_type key'leri (elektrik/su/hava/internet) hem de salon_hizmet_tipi
// kodlarıyla (backlog #100/#101) eşleşsin diye küçük harfle tutuluyor.
export const TEKNIK_ICONS = { elektrik: Zap, su: Droplet, hava: Wind, internet: Wifi };
